import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { Monitor, Moon, Sun } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { isTheme } from '../theme/theme';
import type { Theme } from '../theme/theme';
import { useTheme } from '../theme/useTheme';
import { cn } from '../utils/cn';

const OPTIONS: { value: Theme; icon: LucideIcon }[] = [
    { value: 'light', icon: Sun },
    { value: 'dark', icon: Moon },
    { value: 'system', icon: Monitor },
];

/**
 * Barre tout en haut de l'écran, au-dessus du header, dans les deux variantes du layout.
 * Elle règle l'affichage du site (thème clair / sombre / système). Radix ToggleGroup fournit
 * la sémantique (groupe de boutons radio) et la navigation clavier (flèches).
 */
export default function VisibilityBar() {
    const { t } = useTranslation();
    const { theme, setTheme } = useTheme();

    return (
        <div className="pointer-events-auto border-b border-gray-200 bg-gray-100/90 text-xs text-gray-700 backdrop-blur dark:border-gray-800 dark:bg-gray-900/90 dark:text-gray-300">
            <div className="mx-auto flex max-w-7xl items-center justify-end gap-3 px-3 py-1 sm:justify-between sm:px-4 lg:px-8">
                <p className="max-sm:sr-only">{t('layout.visibility.label')}</p>
                <ToggleGroup.Root
                    type="single"
                    value={theme}
                    // Radix renvoie '' quand on clique l'option déjà active : un thème reste toujours choisi.
                    onValueChange={(value) => isTheme(value) && setTheme(value)}
                    aria-label={t('layout.visibility.theme')}
                    className="flex gap-1"
                >
                    {OPTIONS.map(({ value, icon: Icon }) => (
                        <ToggleGroup.Item
                            key={value}
                            value={value}
                            className={cn(
                                'inline-flex items-center gap-1.5 rounded px-2 py-1 hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gray-900 dark:hover:bg-gray-800 dark:focus-visible:outline-white',
                                'data-[state=on]:bg-gray-900 data-[state=on]:text-white dark:data-[state=on]:bg-white dark:data-[state=on]:text-gray-900',
                            )}
                        >
                            <Icon aria-hidden="true" className="size-3.5" />
                            <span className="max-sm:sr-only">
                                {t(`layout.visibility.${value}`)}
                            </span>
                        </ToggleGroup.Item>
                    ))}
                </ToggleGroup.Root>
            </div>
        </div>
    );
}
